import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2, RotateCcw, AlertTriangle } from "lucide-react";
import { Dialog } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { toast } from "sonner";

interface ReopenJobDialogProps {
    open: boolean;
    onClose: () => void;
    jobId: string;
    /** Shown in the dialog body for context (e.g. "Job #1042"). */
    jobLabel?: string | null;
    /** Called after a successful reopen so the caller can navigate/refresh. */
    onReopened?: () => void;
}

/**
 * Confirmation dialog for reopening a cancelled job (#937). Mirrors
 * CancelJobDialog: the reason is optional here (undoing a mistake shouldn't be
 * harder than making it) and the server records who/when either way.
 *
 * The duplicate guard is the interesting part. Most cancels are dedup cancels,
 * so the server 409s DUPLICATE_WO_ACTIVE when another live job shares the work
 * order number. We surface that inline as a warning with a confirm-anyway
 * button (which retries with `force`) rather than a dead end — the office
 * usually knows which twin should survive.
 */
export function ReopenJobDialog({ open, onClose, jobId, jobLabel, onReopened }: ReopenJobDialogProps) {
    const qc = useQueryClient();
    const [reason, setReason] = useState("");
    const [duplicateWarning, setDuplicateWarning] = useState<string | null>(null);

    const close = () => {
        setReason("");
        setDuplicateWarning(null);
        onClose();
    };

    const reopenJob = useMutation({
        mutationFn: (force: boolean) =>
            api.post(`/api/jobs/${jobId}/reopen`, { reason: reason.trim() || undefined, force }),
        onSuccess: () => {
            toast.success("Job reopened — it's back in Ready to Schedule");
            qc.invalidateQueries({ queryKey: ["jobs"] });
            qc.invalidateQueries({ queryKey: ["job", jobId] });
            qc.invalidateQueries({ queryKey: ["dispatch"] });
            onReopened?.();
            close();
        },
        onError: (err: any) => {
            const code = err?.body?.error;
            const msg = err?.body?.message ?? err?.message ?? "unknown error";
            if (code === "DUPLICATE_WO_ACTIVE") {
                // Not a failure the user needs a toast for — it's a question,
                // and the answer belongs in the dialog they're already reading.
                setDuplicateWarning(msg);
                return;
            }
            toast.error(`Reopen failed: ${msg}`);
        },
    });

    return (
        <Dialog open={open} onClose={close} title="Reopen Job">
            <div className="space-y-4">
                <p className="text-sm text-foreground/80">
                    Reopen {jobLabel ? <span className="font-mono font-medium">{jobLabel}</span> : "this job"}? It goes
                    back to <span className="font-medium">Ready to Schedule</span>. The tech and appointment were
                    cleared when it was cancelled, so you'll need to schedule it again.
                </p>

                {duplicateWarning && (
                    <div className="flex gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 p-3">
                        <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600 mt-0.5" />
                        <p className="text-sm text-foreground/90">{duplicateWarning}</p>
                    </div>
                )}

                <div className="space-y-1.5">
                    <Label htmlFor="reopen-reason">Reason (optional)</Label>
                    <Textarea
                        id="reopen-reason"
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        placeholder="Why is this job coming back? (e.g. cancelled by mistake, customer called back)"
                        rows={3}
                    />
                </div>

                <div className="flex justify-end gap-2 pt-1">
                    <Button variant="outline" onClick={close} disabled={reopenJob.isPending}>
                        Keep Cancelled
                    </Button>
                    <Button disabled={reopenJob.isPending} onClick={() => reopenJob.mutate(duplicateWarning !== null)}>
                        {reopenJob.isPending ? (
                            <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                        ) : (
                            <RotateCcw className="h-4 w-4 mr-1" />
                        )}
                        {duplicateWarning ? "Reopen anyway" : "Reopen Job"}
                    </Button>
                </div>
            </div>
        </Dialog>
    );
}
